import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { 
  LayoutDashboard, 
  MessageSquare, 
  Kanban, 
  Users, 
  Settings, 
  LogOut,
  Bell,
  Zap,
  FileSpreadsheet,
  MessageSquareText
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';

const Sidebar = () => {
  const { user, logout, isAdmin } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/inbox', icon: MessageSquare, label: 'Bandeja' },
    { to: '/pipeline', icon: Kanban, label: 'Pipeline' },
    { to: '/contacts', icon: Users, label: 'Contactos' },
    { to: '/templates', icon: MessageSquareText, label: 'Plantillas', adminOnly: true },
    { to: '/reports', icon: FileSpreadsheet, label: 'Reportes', adminOnly: true },
    { to: '/settings', icon: Settings, label: 'Configuración' },
  ].filter(item => !item.adminOnly || isAdmin);

  return (
    <aside 
      style={{ 
        width: '220px', 
        minWidth: '220px',
        maxWidth: '220px',
        backgroundColor: '#ffffff',
        borderRight: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        flexShrink: 0
      }}
    >
      {/* Logo Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '20px 16px',
        borderBottom: '1px solid #e2e8f0'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          backgroundColor: '#25D366',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <Zap style={{ width: '22px', height: '22px', color: 'white' }} />
        </div>
        <div>
          <div style={{ 
            fontSize: '16px', 
            fontWeight: '700', 
            color: '#0f172a',
            fontFamily: 'Outfit, sans-serif',
            lineHeight: '1.2'
          }}>
            LeadGen
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: '#64748b',
            lineHeight: '1.2'
          }}>
            TenaTour
          </div>
        </div>
      </div>

      {/* Navigation Items */}
      <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto' }}>
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              style={({ isActive }) => ({
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                marginBottom: '4px',
                borderRadius: '8px',
                textDecoration: 'none',
                backgroundColor: isActive ? '#f0fdf4' : 'transparent',
                color: isActive ? '#25D366' : '#475569',
                fontWeight: isActive ? '600' : '500',
                fontSize: '14px',
                transition: 'all 0.15s ease'
              })}
              onMouseEnter={(e) => {
                if (!e.currentTarget.classList.contains('active')) {
                  e.currentTarget.style.backgroundColor = '#f8fafc';
                }
              }}
              onMouseLeave={(e) => {
                const isActive = e.currentTarget.getAttribute('aria-current') === 'page';
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <Icon style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}

        {/* Notifications Item */}
        <NavLink
          to="/settings"
          data-testid="nav-notificaciones"
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            marginBottom: '4px',
            borderRadius: '8px',
            textDecoration: 'none',
            backgroundColor: 'transparent',
            color: '#475569',
            fontWeight: '500',
            fontSize: '14px',
            transition: 'all 0.15s ease'
          }}
        >
          <div style={{ position: 'relative' }}>
            <Bell style={{ width: '20px', height: '20px', flexShrink: 0 }} />
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '-6px',
                right: '-6px',
                backgroundColor: '#25D366',
                color: 'white',
                fontSize: '10px',
                fontWeight: '600',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
          <span>Notificaciones</span>
        </NavLink>
      </nav>

      {/* User Section */}
      <div style={{
        padding: '16px',
        borderTop: '1px solid #e2e8f0',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Avatar className="h-10 w-10" style={{ flexShrink: 0 }}>
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback style={{ 
              backgroundColor: '#25D366', 
              color: 'white',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              {user?.name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#0f172a',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {user?.name}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#64748b'
            }}>
              {user?.role === 'admin' ? 'Admin' : 'Agente'}
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: 'transparent',
              color: '#94a3b8',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#fee2e2';
              e.currentTarget.style.color = '#dc2626';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#94a3b8';
            }}
            title="Cerrar sesión"
          >
            <LogOut style={{ width: '18px', height: '18px' }} />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
